import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { OrderDirection, TemplateObjectType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import type { SearchProps } from 'webapp/utils/filter';

import type { FilterSortSettings } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';
import type { FilterOption } from 'webapp/models-custom/filter-sort/filter';
import type Template from 'webapp/models-custom/template';

export default class FilterSortPublicTemplate extends FilterSort<Template> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/templates') declare mmTemplatesService: Service.ModelManager.Templates;
	@service('constants') declare constantsService: Service.Constants;

	@tracked override sortProp = 'usedCount';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	override itemDomSelector = 'li.filter-sort-item';

	override searchProps: SearchProps = {
		title: 5,
		keywords: 3,
		description: 2,
		metricsNames: 1,
	};

	override typedFilterEventName = TrackingEvent.TEMPLATE_SEARCH;

	override saveFilterInUrlHash = true;

	typeOptions: FilterOption<string>[] = [
		{ id: 'all', text: 'All' },
		{ id: TemplateObjectType.BOARD.toString(), text: 'Dashboards' }, // stringify TemplateObjectType enum to support default value in filter
		{ id: TemplateObjectType.BOARD_LOOP.toString(), text: 'Looped dashboards' }, // stringify TemplateObjectType enum to support default value in filter
	];

	constructor(options: FilterSortSettings<Template>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();

		if (this.allItems.length) {
			this.typedFilterEventData = {
				objectType: this.mmTemplatesService.getTemplateObjectTypeString(
					this.allItems[0]!.objectType,
				),
			};
		}

		// Category
		this.createFilter<string>({
			id: 1,
			label: 'Category',
			name: 'category',
			selected: selectedFilters[1] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.categoryOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedValue) => {
					const category = this.constantsService.templateCategories.find(
						(categoryData) => {
							const compactId = this.getCompactString(categoryData.label);
							return selectedValue === compactId;
						},
					);

					return (
						!!category &&
						item.categories
							.map((categoryId: number) => Math.trunc(categoryId / 100) * 100)
							.includes(category.value)
					); // mapping to match subcategories with main categories
				});
			},
		});

		// Data source (used only)
		this.createFilter<string>({
			id: 2,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[2] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((selectedValue) =>
					item.specificSourceTypesKeys.includes(selectedValue),
				);
			},
		});

		if (this.allItems[0]!.objectType !== TemplateObjectType.REPORT) {
			// Type
			this.createFilter<string>({
				id: 3,
				label: 'Type',
				name: 'type',
				selected: selectedFilters[3] as string | undefined,
				stringValues: true,
				isMultiSelect: true,
				getOptions: () => {
					return this.typeOptions;
				},
				filterFn: function (item, selected) {
					return selected.some(
						(selectedValue) =>
							selectedValue === 'all' || item.objectType.toString() === selectedValue,
					); // stringify TemplateObjectType enum to support default value in filter
				},
			});
		}
	}

	get categoryOptions(): FilterOption<string>[] {
		const categories = this.constantsService.templateCategories
			.filter((cat) => cat.value % 100 === 0)
			.map((cat) => {
				return {
					id: this.getCompactString(cat.label),
					text: cat.label,
				};
			});

		categories.unshift({ id: 'all', text: 'All' });

		return categories;
	}

	@cached
	get datasourceOptions(): FilterOption<string>[] {
		let allItems: Template[] = [];
		// get how many boards each source is used on
		const sourceTypeArray: string[] = [];
		const allItemsObjectType = this.allItems[0]!.objectType;

		// Using public templates models instead of filter sort allItems
		// because of recommended section at board public templates.
		// Two instances of filter sort causes that allItems are only from
		// second instance and if there is one template which belongs to
		// one source type and this template is in recommended section, will not be
		// present in second filter sort instace (not shown in data source filter)
		if (allItemsObjectType === TemplateObjectType.BOARD) {
			allItems = this.mmTemplatesService.boardPublicTemplateModels;
		} else if (allItemsObjectType === TemplateObjectType.BOARD_LOOP) {
			allItems = this.mmTemplatesService.boardLoopPublicTemplateModels;
		} else {
			allItems = this.mmTemplatesService.reportPublicTemplateModels;
		}

		allItems.forEach((template) => {
			template.specificSourceTypesKeys.forEach((sourceType: string) => {
				sourceTypeArray.push(sourceType);
			});
		});

		const list = this.mmSourceTypesService.getSourceTypesListWithCount(sourceTypeArray);

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}
}
